import { ModalPortal } from './ModalPortal';
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Plus,
  Search,
  Trash2,
  Paperclip,
  Calendar,
  X,
  Check,
  AlertCircle,
  Upload,
  Edit,
  PieChart,
  AlertTriangle,
  Clock,
  Eye,
  Layers,
  Truck,
  Camera,
  CheckCircle,
  RotateCcw,
  ExternalLink,
  Calculator,
  FileUp,
} from "lucide-react";
import { compressImage } from "../utils/imageCompressor";
import { mockBackend, Expense, Revenue } from "../services/mockBackend";
import { GoogleGenAI, Type } from "@google/genai";
import { extractDataWithAI } from "../services/aiService";
import Barcode from "react-barcode";
import { AlertModal } from './AlertModal';
import { getLocalDateString } from '../src/lib/utils';

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // --- Mês Selecionado para o Dashboard (Visão Geral) ---
  const [selectedMonth, setSelectedMonth] = useState(
    getLocalDateString().slice(0, 7),
  );

  // Calcula datas de início e fim com base no mês selecionado
  const { startOfMonth, endOfMonth } = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    // Dia 0 do mês seguinte retorna o último dia do mês atual
    const lastDay = new Date(y, m, 0).getDate();
    return {
      startOfMonth: `${y}-${String(m).padStart(2, "0")}-01`,
      endOfMonth: `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
    };
  }, [selectedMonth]);

  // Cálculo da semana atual para o filtro padrão da TABELA (entries) - Domingo a Sábado
  const curr = new Date();
  const first = curr.getDate() - curr.getDay();
  const startOfWeek = getLocalDateString(new Date(curr.setDate(first)));
  const endOfWeek = getLocalDateString(new Date(curr.setDate(first + 6)));

  const [activeTab, setActiveTab] = useState<"overview" | "entries">(
    "overview",
  );
  const [agendaFilter, setAgendaFilter] = useState<'all' | 'open' | 'paid' | 'overdue'>('all');
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  const [isLoading, setIsLoading] = useState(true);

  const [alertConfig, setAlertConfig] = useState<{isOpen: boolean, message: string, type: 'error'|'success'|'info'}>({isOpen: false, message: '', type: 'error'});
  const showAlert = (message: string, type: 'error'|'success'|'info' = 'error') => setAlertConfig({isOpen: true, message, type});

  // Filtros iniciais configurados para o MÊS ATUAL e status TODOS
  const [filters, setFilters] = useState({
    dateStart: startOfMonth,
    dateEnd: endOfMonth,
    status: "all",
    type: "all",
    category: "all",
    search: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState(false); // Estado para controlar visualização forçada
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);

  const [currentExpense, setCurrentExpense] = useState<Partial<Expense>>({
    type: "",
    status: "open",
    quantidade: 1,
    dueDate: getLocalDateString(),
    previsao_pagamento: getLocalDateString(),
    createdAt: getLocalDateString(),
    attachments: [],
  });

  // Estado para controlar o input de valor formatado (Ex: 0,00)
  const [amountInput, setAmountInput] = useState("0,00");
  const [paidAmountInput, setPaidAmountInput] = useState("0,00");

  const [installments, setInstallments] = useState(1);
  const [tempAttachment, setTempAttachment] = useState<{
    name: string;
    url: string;
    thumbnailUrl?: string;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);

  // Estado para Preview de Anexo
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handlePreview = (att: { downloadURL: string, thumbnailUrl?: string, name: string }) => {
    const isPdf = att.name.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      setPreviewUrl(att.downloadURL);
      return;
    }
    
    if (att.thumbnailUrl) {
      setPreviewUrl(att.thumbnailUrl.replace(/=s\d+/, '=s2000').replace(/sz=w\d+/, 'sz=w2000'));
      return;
    }

    // Fallback for old Google Drive links
    if (att.downloadURL.includes('drive.google.com/file/d/')) {
      const match = att.downloadURL.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        setPreviewUrl(`https://drive.google.com/thumbnail?id=${match[1]}&sz=w2000`);
        return;
      }
    } else if (att.downloadURL.includes('drive.google.com/uc?id=')) {
      const match = att.downloadURL.match(/id=([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        setPreviewUrl(`https://drive.google.com/thumbnail?id=${match[1]}&sz=w2000`);
        return;
      }
    }
    
    setPreviewUrl(att.downloadURL);
  };

  // Estados para Modal de Pagamento
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [payingExpense, setPayingExpense] = useState<Expense | null>(null);
  const [paidValue, setPaidValue] = useState<number>(0);
  const [paidValueInput, setPaidValueInput] = useState<string>("0,00");
  const [paidDate, setPaidDate] = useState<string>(
    getLocalDateString(),
  );

  // Estados para Modal de Reversão de Pagamento
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [revertingExpense, setRevertingExpense] = useState<Expense | null>(
    null,
  );

  useEffect(() => {
    if (user) {
      const unsubscribeExp = mockBackend.subscribeToExpenses(
        user,
        (expenses) => {
          setExpenses(expenses);
        },
      );
      const unsubscribeRev = mockBackend.subscribeToRevenues(
        user,
        (revenues) => {
          setRevenues(revenues);
        },
      );
      refreshSettings();
      return () => {
        unsubscribeExp();
        unsubscribeRev();
      };
    }
  }, [user]);

  const canEdit = (expense: Expense | Partial<Expense>) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!expense.id) return true;
    return expense.createdBy === user.id;
  };

  const canDelete = () => {
    return user?.role === "admin";
  };

  const filteredExpenses = useMemo(() => {
    let result = [...expenses];
    if (filters.status !== "all")
      result = result.filter((e) => e.status === filters.status);
    if (filters.type !== "all")
      result = result.filter((e) => e.type === filters.type);
    if (filters.category !== "all")
      result = result.filter((e) => e.category === filters.category);

    // Filtro de Data aprimorado: Usa Data de Pagamento se pago, ou Vencimento/Criação se não
    if (filters.dateStart) {
      result = result.filter((e) => {
        const dateRef =
          e.status === "paid" && e.paymentDate
            ? e.paymentDate
            : e.dueDate || e.createdAt;
        return dateRef >= filters.dateStart;
      });
    }
    if (filters.dateEnd) {
      result = result.filter((e) => {
        const dateRef =
          e.status === "paid" && e.paymentDate
            ? e.paymentDate
            : e.dueDate || e.createdAt;
        return dateRef <= filters.dateEnd;
      });
    }

    if (filters.search) {
      const term = (filters.search || "").toLowerCase();
      result = result.filter(
        (e) =>
          (e.title || "").toLowerCase().includes(term) ||
          (e.supplierName || "").toLowerCase().includes(term) ||
          String(e.amount).includes(term),
      );
    }

    // Ordenação: Ordem de inserção (createdAt) decrescente (mais recentes primeiro)
    result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return result;
  }, [expenses, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const dashboardMetrics = useMemo(() => {
    // Filtra despesas APENAS do mês selecionado para os cards (Independente do filtro da tabela)
    const currentMonthExpenses = expenses.filter((e) => {
      // Se pago, usa data de pagamento (Caixa). Se aberto/vencido, usa vencimento (Competência).
      const dateToCheck =
        e.status === "paid"
          ? e.paymentDate || e.dueDate
          : e.dueDate || e.createdAt;
      if (!dateToCheck) return false;
      const dateStr = dateToCheck.split("T")[0];
      return dateStr >= startOfMonth && dateStr <= endOfMonth;
    });

    const overdueExpenses = currentMonthExpenses.filter(
      (e) => e.status === "overdue",
    );
    const openExpenses = currentMonthExpenses.filter(
      (e) => e.status === "open",
    );
    const paidExpenses = currentMonthExpenses.filter(
      (e) => e.status === "paid",
    );

    const totalOverdue = overdueExpenses.reduce((sum, e) => sum + e.amount, 0);
    const totalOpen = openExpenses.reduce((sum, e) => sum + e.amount, 0);
    const totalPaid = paidExpenses.reduce(
      (sum, e) => sum + (e.paidAmount || e.amount),
      0,
    );

    // Agenda: Lógica de exibição baseada no mês selecionado
    // Mês Atual: Somente vencimentos (Abertos e Vencidos)
    // Meses Posteriores: Abertos e Pagos
    // Meses Anteriores: Pagos e Vencidos

    const todayStr = getLocalDateString().slice(0, 7); // YYYY-MM
    let agendaList = [...currentMonthExpenses];

    if (selectedMonth < todayStr) {
      // Passado: Pagos e Vencidos (Remove Abertos simples, que deveriam ser vencidos ou pagos)
      agendaList = agendaList.filter(
        (e) => e.status === "paid" || e.status === "overdue",
      );
    } else if (selectedMonth > todayStr) {
      // Futuro: Abertos e Pagos (Remove Vencidos, pois é futuro)
      agendaList = agendaList.filter(
        (e) => e.status === "open" || e.status === "paid",
      );
    } else {
      // Presente (Mês Atual): Somente Vencimentos (Abertos e Vencidos) - Remove Pagos da lista visual
      agendaList = agendaList.filter(
        (e) => e.status === "open" || e.status === "overdue",
      );
    }

    // Ordenação por data
    agendaList.sort((a, b) => {
      const dateA =
        a.status === "paid" && a.paymentDate
          ? a.paymentDate
          : a.dueDate || a.createdAt;
      const dateB =
        b.status === "paid" && b.paymentDate
          ? b.paymentDate
          : b.dueDate || b.createdAt;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA.localeCompare(dateB);
    });

    const categoryData: Record<string, number> = {};
    currentMonthExpenses.forEach((e) => {
      const val = e.status === "paid" ? e.paidAmount || e.amount : e.amount;
      categoryData[e.category] = (categoryData[e.category] || 0) + val;
    });
    const maxVal = Math.max(...Object.values(categoryData), 1);

    const currentMonthRevenues = revenues.filter((e) => {
      const dateToCheck =
        e.status === "RECEBIDO"
          ? e.data_recebimento || e.previsao_pagamento
          : e.previsao_pagamento || e.data_emissao || e.created_at;
      if (!dateToCheck) return false;
      const dateStr = dateToCheck.split("T")[0];
      return dateStr >= startOfMonth && dateStr <= endOfMonth;
    });
    const paidRevenues = currentMonthRevenues.filter(
      (e) => e.status === "RECEBIDO",
    );
    const totalRevenuesPaid = paidRevenues.reduce(
      (sum, e) => sum + e.valor_liquido,
      0,
    );
    const realProfit = totalRevenuesPaid - totalPaid;

    return {
      totalOverdue,
      totalOpen,
      totalPaid,
      agenda: agendaList,
      categoryData,
      maxVal,
      realProfit,
    };
  }, [expenses, revenues, startOfMonth, endOfMonth, selectedMonth]);

  // Calcula o total dos itens filtrados para exibição na aba entries
  const entriesTotalAmount = useMemo(() => {
    if (!Array.isArray(filteredExpenses)) return 0;
    return filteredExpenses.reduce(
      (sum, exp) => sum + (Number(exp.amount) || 0),
      0,
    );
  }, [filteredExpenses]);

  const refreshSettings = async () => {
    const cats = await mockBackend.getCategories();
    const typs = await mockBackend.getTypes();
    const vecs = await mockBackend.getVehicles();
    setCategories(cats);
    setTypes(typs);
    setVehicles(vecs);
    setIsLoading(false);
  };

  const refreshData = async () => {
    setIsLoading(true);
    try {
      // Expenses are handled by subscription
      await refreshSettings();
    } finally {
      setIsLoading(false);
    }
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    if (!canDelete()) return;
    setExpenseToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;
    try {
      await mockBackend.deleteExpense(user!, expenseToDelete);
      setExpenses((prev) => prev.filter((e) => e.id !== expenseToDelete));
      setIsDeleteModalOpen(false);
      setExpenseToDelete(null);
    } catch (e: any) {
      showAlert(e.message);
    }
  };

  // --- Função para tratar Input de Valor (Máscara de Moeda) ---
  const handleAmountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, ""); // Remove tudo que não é dígito
    const numberValue = parseInt(rawValue || "0", 10) / 100; // Divide por 100 para decimal

    // Atualiza o visual
    setAmountInput(
      numberValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

    // Atualiza o estado da despesa com o número real
    setCurrentExpense((prev) => ({ ...prev, amount: numberValue }));
  };

  const handlePaidAmountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, ""); // Remove tudo que não é dígito
    const numberValue = parseInt(rawValue || "0", 10) / 100; // Divide por 100 para decimal

    // Atualiza o visual
    setPaidAmountInput(
      numberValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

    // Atualiza o estado da despesa com o número real
    setCurrentExpense((prev) => ({ ...prev, paidAmount: numberValue }));
  };

  const openModal = (expense?: Expense, forceViewMode = false) => {
    setTempAttachment(null);
    setViewMode(forceViewMode);

    if (expense) {
      setIsEditing(true);
      setCurrentExpense(JSON.parse(JSON.stringify(expense)));
      setAmountInput(
        (expense.amount || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
      setPaidAmountInput(
        (expense.paidAmount || expense.amount || 0).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
      setInstallments(1);
    } else {
      setIsEditing(false);
      setInstallments(1);
      setAmountInput("0,00");
      setPaidAmountInput("0,00");
      const todayStr = getLocalDateString();
      setCurrentExpense({
        type: types[0] || "Boleto",
        status: "open",
        quantidade: 1,
        category: categories[0] || "Geral",
        dueDate: todayStr,
        previsao_pagamento: todayStr,
        createdAt: todayStr,
        attachments: [],
      });
      setAmountInput("0,00");
    }
    setIsModalOpen(true);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    let newCategory = currentExpense.category;
    if (newType === "Gasolina" || newType === "Combustível") {
      newCategory = "Transporte";
    }
    setCurrentExpense((prev) => ({
      ...prev,
      type: newType,
      category: newCategory,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit(currentExpense)) return;
    setIsSaving(true);
    try {
      let mainExpenseId = "";

      // Parse do valor final garantindo que vem do input mascarado se necessário
      const finalAmount =
        parseFloat(amountInput.replace(/\./g, "").replace(",", ".")) || 0;

      const expenseToSave = {
        ...currentExpense,
        amount: finalAmount,
      };

      // Se estiver marcado como pago, garante que tem data e valor pago
      if (expenseToSave.status === "paid") {
        if (!expenseToSave.paidAmount) expenseToSave.paidAmount = finalAmount;
        if (!expenseToSave.paymentDate)
          expenseToSave.paymentDate =
            expenseToSave.dueDate || expenseToSave.createdAt;
      }

      if (isEditing && expenseToSave.id) {
        await mockBackend.updateExpense(user!, expenseToSave.id, expenseToSave);
        mainExpenseId = expenseToSave.id;
      } else {
        if (expenseToSave.type === "Boleto" && installments > 1) {
          const baseDueDate = expenseToSave.dueDate
            ? new Date(expenseToSave.dueDate + "T12:00:00")
            : new Date();
          const baseTitle = expenseToSave.title || "Despesa";
          const originalDay = baseDueDate.getDate();
          for (let i = 0; i < installments; i++) {
            const newDate = new Date(baseDueDate);
            newDate.setMonth(baseDueDate.getMonth() + i);
            if (newDate.getDate() !== originalDay) {
              newDate.setDate(0);
            }
            const created = await mockBackend.createExpense(user!, {
              ...expenseToSave,
              title: `${baseTitle} (${i + 1}/${installments})`,
              dueDate: expenseToSave.dueDate
                ? getLocalDateString(newDate)
                : undefined,
            });
            if (i === 0) mainExpenseId = created.id;
          }
        } else {
          const created = await mockBackend.createExpense(user!, expenseToSave);
          mainExpenseId = created.id;
        }
      }
      if (tempAttachment && mainExpenseId) {
        const newAtt = await mockBackend.addAttachment(
          user!,
          mainExpenseId,
          tempAttachment.url,
          tempAttachment.name,
          "comprovante",
          user?.id,
          tempAttachment.thumbnailUrl
        );
        setExpenses((prev) =>
          prev.map((e) =>
            e.id === mainExpenseId
              ? { ...e, attachments: [...(e.attachments || []), newAtt] }
              : e
          )
        );
      }
      setIsModalOpen(false);
      setDocumentMetadata(null);
      refreshData();
    } catch (e: any) {
      showAlert("Erro ao salvar: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePdfUploadClick = () => {
    pdfInputRef.current?.click();
  };

  const handlePdfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPdf(true);
    try {
      const base64EncodeString = await new Promise<string>(
        (resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(",")[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        },
      );

      const mimeType = file.type === "application/pdf" ? "application/pdf" : "image/jpeg";
      const prompt = "Extraia os dados deste boleto, nota fiscal ou comprovante de pagamento (incluindo comprovantes de PIX). Para comprovantes de PIX ou transferências, o título (title) e a descrição (description) devem conter o nome do banco e o nome da pessoa/empresa recebedora do pagamento. O tipo (type) deve ser sempre 'Boleto'. A categoria (category) deve ser sempre 'Compras'. ATENÇÃO: Se for um comprovante de PIX ou pagamento, NÃO gere código de barras falso nem retorne 'NULL', deixe vazio. Se for um boleto, extraia com precisão absoluta o código de barras (codigo_barras) e a linha digitável (linha_digitavel). Se o boleto tiver QR Code PIX ou chave PIX Copia e Cola, extraia para o campo 'pix'. Extraia também o fornecedor (supplierName), valor (amount) e vencimento (dueDate). Retorne um JSON.";
      const schema = {
        type: Type.OBJECT,
        properties: {
          docType: {
            type: Type.STRING,
            description: "Tipo do documento (ex: boleto, comprovante_pix, nota_fiscal, outro)",
          },
          supplierName: {
            type: Type.STRING,
            description: "Nome ou Razão Social do fornecedor/recebedor",
          },
          title: {
            type: Type.STRING,
            description: "Título curto ou descrição do serviço/produto",
          },
          description: {
            type: Type.STRING,
            description: "Descrição detalhada",
          },
          amount: { type: Type.NUMBER, description: "Valor total a pagar" },
          quantidade: { type: Type.NUMBER, description: "Quantidade de itens se indicada no documento" },
          dueDate: {
            type: Type.STRING,
            description: "Data de vencimento no formato YYYY-MM-DD",
          },
          previsao_pagamento: {
            type: Type.STRING,
            description: "Previsão de pagamento no formato YYYY-MM-DD",
          },
          codigo_barras: {
            type: Type.STRING,
            description: "Código de barras do boleto (somente números)",
          },
          linha_digitavel: {
            type: Type.STRING,
            description: "Linha digitável do boleto (somente números)",
          },
          pix: {
            type: Type.STRING,
            description: "Código PIX Copia e Cola ou chave PIX, se houver no boleto",
          },
        },
      };

      // Inicia o upload em paralelo com a extração da IA
      const uploadPromise = (async () => {
        try {
          let uploadBase64 = base64EncodeString;
          if (file.type.startsWith('image/')) {
            uploadBase64 = await compressImage(file);
          }
          return await mockBackend.uploadImage(uploadBase64, file.name, file.type);
        } catch (e) {
          console.error("Failed to upload AI attachment", e);
          return null;
        }
      })();

      const extractedData = await extractDataWithAI(base64EncodeString, mimeType, prompt, schema);
      const uploadResult = await uploadPromise;

      const docType = extractedData.docType;
      const expenseData: any = {};
      if (extractedData.amount !== undefined) expenseData.amount = extractedData.amount;
      if (extractedData.quantidade !== undefined) expenseData.quantidade = extractedData.quantidade;
      if (extractedData.dueDate) expenseData.dueDate = extractedData.dueDate;
      if (extractedData.previsao_pagamento) expenseData.previsao_pagamento = extractedData.previsao_pagamento;
      if (extractedData.supplierName) expenseData.supplierName = extractedData.supplierName;
      if (extractedData.codigo_barras) expenseData.codigo_barras = extractedData.codigo_barras;
      if (extractedData.linha_digitavel) expenseData.linha_digitavel = extractedData.linha_digitavel;
      if (extractedData.pix) expenseData.pix = extractedData.pix;
      if (extractedData.description) expenseData.description = extractedData.description;
      if (extractedData.title) expenseData.title = extractedData.title;

      const extractedType = docType === 'boleto' ? 'Boleto' : (docType === 'comprovante_pix' ? 'PIX' : 'Geral');
      const extractedCategory = 'Compras';

      setCurrentExpense((prev) => ({
        ...prev,
        ...expenseData,
        type: types.includes(extractedType) ? extractedType : (prev.type || types[0] || "Boleto"),
        category: categories.includes(extractedCategory) ? extractedCategory : (prev.category || categories[0] || "Geral"),
        createdAt: getLocalDateString(),
      }));

      if (expenseData.amount !== undefined) {
        setAmountInput(
          expenseData.amount.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        );
      }

      if (uploadResult) {
        setTempAttachment({ name: file.name, url: uploadResult.url, thumbnailUrl: uploadResult.thumbnailUrl });
      } else {
        const localUrl = URL.createObjectURL(file);
        setTempAttachment({ name: file.name, url: localUrl });
      }

      if (extractedData._metadata) {
        setDocumentMetadata(extractedData._metadata);
      }

      showAlert(
        "Dados extraídos com sucesso via IA. Por favor, revise os campos antes de salvar.",
        "success"
      );

      if (!isModalOpen) {
        setIsModalOpen(true);
        setIsEditing(false);
      }
    } catch (err: any) {
      showAlert("Erro ao ler documento com IA: " + err.message);
    } finally {
      setIsUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (currentExpense.id && !canEdit(currentExpense)) return;

    // Preview instantâneo
    const localUrl = URL.createObjectURL(file);
    setTempAttachment({ name: file.name, url: localUrl });

    setIsUploading(true);
    try {
      const compressedBase64 = await compressImage(file);
      const uploadResult = await mockBackend.uploadImage(
        compressedBase64,
        file.name,
        file.type
      );
      if (currentExpense.id) {
        const newAtt = await mockBackend.addAttachment(
          user!,
          currentExpense.id!,
          uploadResult.url,
          file.name,
          "comprovante",
          user?.id,
          uploadResult.thumbnailUrl
        );
        setCurrentExpense((prev) => ({
          ...prev,
          attachments: [...(prev.attachments || []), newAtt],
        }));
        setExpenses((prev) =>
          prev.map((e) =>
            e.id === currentExpense.id
              ? { ...e, attachments: [...(e.attachments || []), newAtt] }
              : e
          )
        );
        refreshData();
        setTempAttachment(null); // Limpa o preview após sucesso
      } else {
        setTempAttachment({ name: file.name, url: uploadResult.url, thumbnailUrl: uploadResult.thumbnailUrl });
      }
    } catch (err: any) {
      showAlert(err.message);
      setTempAttachment(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteAttachment = async (attId: string) => {
    if (!canEdit(currentExpense)) return;
    if (!confirm("Excluir anexo?")) return;
    try {
      await mockBackend.deleteAttachment(user!, attId);
      setCurrentExpense((prev) => ({
        ...prev,
        attachments: prev.attachments?.filter((a) => a.id !== attId),
      }));
      refreshData();
    } catch (err: any) {
      showAlert(err.message);
    }
  };

  const clearTempAttachment = () => {
    setTempAttachment(null);
  };

  const handlePaidValueInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    const numberValue = parseInt(rawValue || "0", 10) / 100;
    setPaidValueInput(
      numberValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );
    setPaidValue(numberValue);
  };

  const handleMarkAsPaid = async (expense: Expense) => {
    if (!canEdit(expense)) return;
    if (expense.status === "paid") {
      setRevertingExpense(expense);
      setIsRevertModalOpen(true);
    } else {
      setPayingExpense(expense);
      setPaidValue(expense.amount);
      setPaidValueInput(
        expense.amount.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
      setPaidDate(getLocalDateString());
      setIsPaymentModalOpen(true);
    }
  };

  const handleConfirmPayment = async () => {
    if (!payingExpense || !user) return;
    try {
      await mockBackend.updateExpense(user, payingExpense.id, {
        status: "paid",
        paymentDate: paidDate,
        paidAmount: paidValue,
      });
      setIsPaymentModalOpen(false);
      refreshData();
    } catch (err: any) {
      showAlert(err.message);
    }
  };

  const handleConfirmRevert = async (newStatus: "open" | "overdue") => {
    if (!revertingExpense || !user) return;
    try {
      await mockBackend.updateExpense(user, revertingExpense.id, {
        status: newStatus,
        paymentDate: null,
        paidAmount: null,
      });
      setIsRevertModalOpen(false);
      refreshData();
    } catch (err: any) {
      showAlert(err.message);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === "paid")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-500/20 shadow-sm">
          <CheckCircle size={12} /> Pago
        </span>
      );
    if (status === "overdue")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20 shadow-sm">
          <AlertCircle size={12} /> Vencido
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20 shadow-sm">
        <Calendar size={12} /> Aberto
      </span>
    );
  };

  const formatCurrency = (val: number | undefined | null) => {
    if (typeof val !== "number" || isNaN(val)) return "R$ 0,00";
    return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "-";
    const dateToParse = dateString.includes("T") ? dateString : `${dateString}T12:00:00`;
    return new Date(dateToParse).toLocaleDateString("pt-BR");
  };

  const isViewOnly =
    viewMode || (currentExpense.id && !canEdit(currentExpense));

  const showVehicleSelect =
    currentExpense.type &&
    ((currentExpense.type || "").toLowerCase().includes("gasolina") ||
      (currentExpense.type || "").toLowerCase().includes("combustível"));

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const paginatedExpenses = filteredExpenses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-4">
      <AlertModal 
        isOpen={alertConfig.isOpen} 
        message={alertConfig.message} 
        type={alertConfig.type} 
        onClose={() => setAlertConfig(prev => ({...prev, isOpen: false}))} 
      />
      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === "overview" ? "border-brand-600 text-brand-700 dark:text-brand-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-200"}`}
        >
          Visão Geral
        </button>
        <button
          onClick={() => setActiveTab("entries")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === "entries" ? "border-brand-600 text-brand-700 dark:text-brand-400" : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-200"}`}
        >
          Lançamentos
        </button>
      </div>

      {activeTab === "overview" && (
        <div className="animate-in fade-in duration-300 space-y-6">
          {/* Seletor de Mês */}
          <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-100 dark:bg-brand-900/50 text-brand-700 dark:text-brand-400 rounded-lg">
                <Calendar size={20} />
              </div>
              <div>
                <h3 className="font-bold text-slate-700 dark:text-slate-200 text-sm">
                  Período de Análise
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Selecione o mês para visualizar os indicadores.
                </p>
              </div>
            </div>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-sm font-bold text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-950 focus:outline-none focus:border-brand-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-red-50 dark:bg-red-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Total Vencido (Mês)
              </h3>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400 z-10">
                {formatCurrency(dashboardMetrics.totalOverdue)}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-blue-50 dark:bg-blue-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Total em Aberto (Mês)
              </h3>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 z-10">
                {formatCurrency(dashboardMetrics.totalOpen)}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-green-50 dark:bg-green-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Total Pago (Mês)
              </h3>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400 z-10">
                {formatCurrency(dashboardMetrics.totalPaid)}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col justify-between h-28 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-purple-50 dark:bg-purple-500/10 rounded-bl-full -mr-2 -mt-2 group-hover:scale-110 transition-transform"></div>
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase z-10">
                Lucro Real (Mês)
              </h3>
              <div
                className={`text-2xl font-bold z-10 ${dashboardMetrics.realProfit >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
              >
                {formatCurrency(dashboardMetrics.realProfit)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col h-[500px] lg:h-[calc(100vh-380px)] min-h-[400px]">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <PieChart
                  size={18}
                  className="text-brand-600 dark:text-brand-400"
                />
                <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200">
                  Despesas por Categoria (Mês)
                </h3>
              </div>
              <div className="space-y-4 flex-1 overflow-y-auto pr-2">
                {Object.entries(dashboardMetrics.categoryData).map(
                  ([cat, val]) => (
                    <div key={cat}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {cat}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400">
                          {formatCurrency(val as number)}
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden relative">
                        <div
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-600"
                          style={{
                            width: `${((val as number) / dashboardMetrics.maxVal) * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  ),
                )}
                {Object.keys(dashboardMetrics.categoryData).length === 0 && (
                  <div className="text-center text-slate-400 dark:text-slate-500 py-8 text-xs">
                    Sem dados para este mês.
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-5 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col h-[500px] lg:h-[calc(100vh-380px)] min-h-[400px]">
              <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <div className="flex items-center gap-2">
                  <Clock
                    size={18}
                    className="text-green-600 dark:text-green-400"
                  />
                  <h3 className="font-bold text-sm text-slate-700 dark:text-slate-200">
                    Agenda de Vencimentos (Mês)
                  </h3>
                </div>
                <select 
                  value={agendaFilter} 
                  onChange={(e) => setAgendaFilter(e.target.value as any)}
                  className="text-xs border border-slate-200 dark:border-slate-700 rounded px-2 py-1 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                >
                  <option value="all">Todos</option>
                  <option value="open">Aberto</option>
                  <option value="paid">Pagos</option>
                  <option value="overdue">Vencidos</option>
                </select>
              </div>
              <div className="space-y-2 flex-1 overflow-y-auto pr-2">
                {dashboardMetrics.agenda.filter(e => agendaFilter === 'all' || e.status === agendaFilter).length === 0 ? (
                  <div className="text-center py-8 text-slate-400 dark:text-slate-500">
                    <Check size={24} className="mx-auto mb-2 opacity-50" />
                    <p className="text-xs">Nenhum lançamento para exibir.</p>
                  </div>
                ) : (
                  dashboardMetrics.agenda.filter(e => agendaFilter === 'all' || e.status === agendaFilter).map((e) => {
                    let bgClass =
                      "bg-blue-50 dark:bg-blue-500/10 border-blue-500";
                    let textClass = "text-blue-900 dark:text-blue-400";
                    let icon = <Clock size={10} />;
                    let label = "Aberto";
                    let dateShow = e.dueDate
                      ? formatDate(e.dueDate)
                      : "N/A";

                    if (e.status === "paid") {
                      bgClass =
                        "bg-green-50 dark:bg-green-500/10 border-green-500";
                      textClass = "text-green-900 dark:text-green-400";
                      icon = <CheckCircle size={10} />;
                      label = "Pago";
                      if (e.paymentDate)
                        dateShow = formatDate(e.paymentDate);
                    } else if (e.status === "overdue") {
                      bgClass = "bg-red-50 dark:bg-red-500/10 border-red-500";
                      textClass = "text-red-900 dark:text-red-400";
                      icon = <AlertTriangle size={10} />;
                      label = "Vencido";
                    }

                    return (
                      <div
                        key={e.id}
                        onClick={() => openModal(e, true)}
                        className={`flex justify-between items-center p-3 border-l-2 rounded text-sm cursor-pointer hover:opacity-80 transition-opacity ${bgClass}`}
                      >
                        <div>
                          <span className={`block font-semibold ${textClass}`}>
                            {e.title}
                          </span>
                          <span
                            className={`text-[10px] font-bold uppercase flex items-center gap-1 ${textClass} opacity-80`}
                          >
                            {icon} {label}: {dateShow}
                          </span>
                        </div>
                        <div className={`font-bold text-sm ${textClass}`}>
                          {formatCurrency(
                            e.status === "paid" && e.paidAmount
                              ? e.paidAmount
                              : e.amount,
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "entries" && (
        <div className="animate-in fade-in duration-300 space-y-4 pb-12">
          <div className="flex flex-col md:flex-row justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-2.5 text-slate-400 dark:text-slate-500"
                size={16}
              />
              <input
                type="text"
                placeholder="Ex: Boleto, Posto Ipiranga, R$ 50..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="w-full pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 rounded text-sm focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              <input
                type="file"
                ref={pdfInputRef}
                onChange={handlePdfChange}
                accept="application/pdf,image/*"
                className="hidden"
              />
              <button
                onClick={handlePdfUploadClick}
                disabled={isUploadingPdf}
                className="bg-blue-600 text-white px-4 py-2 rounded font-bold uppercase text-xs flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
              >
                <FileUp size={16} />{" "}
                {isUploadingPdf ? "Lendo..." : "Preencher via IA"}
              </button>
              <button
                onClick={() => openModal()}
                className="bg-green-600 text-white px-4 py-2 rounded font-bold uppercase text-xs flex items-center gap-2 hover:bg-green-700 transition-colors shadow-sm"
              >
                <Plus size={16} /> Novo
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            >
              <option value="all">Status: Todos</option>
              <option value="open">Aberto</option>
              <option value="paid">Pago</option>
              <option value="overdue">Vencido</option>
            </select>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            >
              <option value="all">Tipo: Todos</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={filters.category}
              onChange={(e) =>
                setFilters({ ...filters, category: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            >
              <option value="all">Categoria: Todas</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={filters.dateStart}
              onChange={(e) =>
                setFilters({ ...filters, dateStart: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            />
            <input
              type="date"
              value={filters.dateEnd}
              onChange={(e) =>
                setFilters({ ...filters, dateEnd: e.target.value })
              }
              className="border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
            />
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 text-xs uppercase border-b border-slate-200 dark:border-slate-800">
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Vencimento</th>
                  <th className="px-4 py-3 font-semibold">Descrição</th>
                  <th className="px-4 py-3 font-semibold">Tipo</th>
                  <th className="px-4 py-3 font-semibold">Categoria</th>
                  <th className="px-4 py-3 font-semibold text-right">Valor</th>
                  <th className="px-4 py-3 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-sm text-slate-700 dark:text-slate-300">
                {paginatedExpenses.map((exp) => (
                  <tr
                    key={exp.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-4 py-2">
                      <div
                        onClick={() => handleMarkAsPaid(exp)}
                        className={`cursor-pointer ${canEdit(exp) ? "hover:opacity-80" : ""}`}
                      >
                        {getStatusBadge(exp.status)}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                      {exp.dueDate
                        ? formatDate(exp.dueDate)
                        : "-"}
                    </td>
                    <td
                      className="px-4 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      onClick={() => openModal(exp, true)}
                      title="Clique para visualizar detalhes"
                    >
                      <div className="font-semibold text-slate-800 dark:text-slate-200">
                        {exp.title}
                      </div>
                      <div className="text-xs text-slate-400 dark:text-slate-500">
                        {exp.supplierName}
                      </div>
                      {exp.vehicle && (
                        <div className="text-[10px] text-orange-600 dark:text-orange-400 flex items-center gap-1 mt-0.5">
                          <Truck size={10} /> {exp.vehicle}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-500/20 font-medium">
                        {exp.type}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                        {exp.category}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="font-mono font-medium text-slate-800 dark:text-slate-200">
                        {formatCurrency(exp.amount)}
                      </div>
                      {exp.quantidade !== undefined && exp.quantidade !== null && exp.quantidade > 1 && (
                        <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                          Qtd: {exp.quantidade}
                        </div>
                      )}
                      {exp.status === "paid" &&
                        exp.paidAmount !== undefined &&
                        exp.paidAmount !== null && (
                          <div className="text-[10px] font-bold text-green-600 dark:text-green-400 mt-0.5">
                            Pago: {formatCurrency(exp.paidAmount)}
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openModal(exp, false)}
                          className="p-1.5 text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 rounded"
                          title={canEdit(exp) ? "Editar" : "Ver"}
                        >
                          {canEdit(exp) ? (
                            <Edit size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                        {canDelete() && (
                          <button
                            onClick={() => handleDeleteClick(exp.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 p-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border border-slate-300 dark:border-slate-700 disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border border-slate-300 dark:border-slate-700 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            )}

            <div className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 px-4 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <Calculator
                  size={18}
                  className="text-brand-600 dark:text-brand-400"
                />
                <span className="text-xs font-bold uppercase tracking-wider">
                  Total dos Lançamentos Filtrados
                </span>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-6 py-2 rounded-lg shadow-inner">
                <span className="text-xl font-bold text-brand-700 dark:text-brand-400 font-mono">
                  {formatCurrency(entriesTotalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-red-600 text-white px-5 py-3 flex justify-between items-center shrink-0">
              <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                <Trash2 size={18} /> Confirmar Exclusão
              </h3>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="hover:text-red-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Confirmar exclusão definitiva?</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Esta ação não poderá ser desfeita. O lançamento será removido permanentemente.</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm text-white bg-red-600 hover:bg-red-700 font-bold uppercase rounded shadow-sm"
                >
                  Sim, Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reversão de Pagamento */}
      {isRevertModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-orange-600 text-white px-5 py-3 flex justify-between items-center shrink-0">
              <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                <RotateCcw size={18} /> Reverter Pagamento
              </h3>
              <button
                onClick={() => setIsRevertModalOpen(false)}
                className="hover:text-orange-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="text-center">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 font-medium">
                  Lançamento:{" "}
                  <span className="text-slate-800 dark:text-slate-200 italic">
                    "{revertingExpense?.title}"
                  </span>
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                  Como você deseja reabrir este lançamento?
                </p>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => handleConfirmRevert("open")}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 font-bold transition-all"
                  >
                    <Calendar size={18} /> Colocar como Aberto (Pendente)
                  </button>
                  <button
                    onClick={() => handleConfirmRevert("overdue")}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 font-bold transition-all"
                  >
                    <AlertCircle size={18} /> Colocar como Vencido
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 px-6 py-4 flex justify-center border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setIsRevertModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 rounded transition-colors font-medium"
              >
                Desistir e manter como Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pagamento (Data e Valor Pago) */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-green-700 text-white px-5 py-3 flex justify-between items-center shrink-0">
              <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                <CheckCircle size={18} /> Confirmar Pagamento
              </h3>
              <button
                onClick={() => setIsPaymentModalOpen(false)}
                className="hover:text-green-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 italic">
                  "{payingExpense?.title}"
                </p>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                  Valor Pago (R$)
                </label>
                <input
                  type="text"
                  className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 p-3 rounded font-bold text-lg text-green-700 dark:text-green-500 focus:outline-none focus:border-green-500"
                  value={paidValueInput}
                  onChange={handlePaidValueInputChange}
                />
                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                  Valor original: {formatCurrency(payingExpense?.amount || 0)}
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                  Data do Pagamento
                </label>
                <input
                  type="date"
                  className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 p-3 rounded text-slate-800 dark:text-slate-200 focus:outline-none focus:border-green-500"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                />
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 px-6 py-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setIsPaymentModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 rounded transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmPayment}
                className="px-6 py-2 bg-green-600 text-white font-bold uppercase text-xs rounded shadow-lg shadow-green-200 dark:shadow-none hover:bg-green-700 transition-all flex items-center gap-2"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 z-[9999] flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-5xl rounded-2xl flex flex-col max-h-[92vh] sm:max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-[#4c1d95] via-[#5b21b6] to-[#3b0764] text-white px-6 py-3.5 flex justify-between items-center shrink-0 shadow-md">
              <h3 className="font-bold text-base flex items-center gap-2 tracking-wide">
                {isEditing ? (
                  isViewOnly ? (
                    <Eye size={18} />
                  ) : (
                    <Edit size={18} />
                  )
                ) : (
                  <Plus size={18} />
                )}
                {isEditing
                  ? isViewOnly
                    ? "Visualizar Lançamento"
                    : "Editar Lançamento"
                  : "Novo Lançamento"}
              </h3>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setDocumentMetadata(null);
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
              {documentMetadata && (
                <div className={`p-4 rounded-xl border ${documentMetadata.precisa_revisao ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/50 dark:text-amber-200' : 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800/50 dark:text-green-200'}`}>
                  <div className="flex items-center gap-2 font-semibold mb-2">
                    {documentMetadata.precisa_revisao ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                    <span>
                      {documentMetadata.precisa_revisao ? 'Revisão Necessária' : 'Extração Confiável'}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p><strong>Tipo de Documento:</strong> {documentMetadata.tipo_documento}</p>
                    <p><strong>Confiança Média:</strong> {(documentMetadata.score_confianca * 100).toFixed(0)}%</p>
                    {documentMetadata.erros_validacao?.length > 0 && (
                      <div className="mt-2 text-red-600 dark:text-red-400">
                        <strong>Avisos de Validação:</strong>
                        <ul className="list-disc pl-5 mt-1">
                          {documentMetadata.erros_validacao.map((erro: string, idx: number) => (
                            <li key={idx}>{erro}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Coluna da Esquerda: Identificação e Dados Gerais (7 colunas) */}
                <div className="lg:col-span-7 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Identificação & Classificação
                  </h4>

                  {/* Input Data do Registro com Sincronização */}
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-600 dark:text-slate-400">
                      Data do Registro:
                    </span>
                    <input
                      type="date"
                      disabled={isViewOnly}
                      className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-2.5 py-1 font-mono text-slate-800 dark:text-slate-200 focus:outline-none focus:border-[#7c3aed]"
                      value={
                        currentExpense.createdAt
                          ? currentExpense.createdAt.split("T")[0]
                          : ""
                      }
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setCurrentExpense((prev) => ({
                          ...prev,
                          createdAt: newDate,
                          dueDate: newDate,
                          paymentDate:
                            prev.status === "paid" ? newDate : prev.paymentDate,
                        }));
                      }}
                    />
                  </div>

                  {/* Título */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Título
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg focus:border-[#7c3aed] focus:outline-none disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                      placeholder="Ex: Manutenção do Ar Condicionado"
                      value={currentExpense.title || ""}
                      onChange={(e) =>
                        setCurrentExpense({
                          ...currentExpense,
                          title: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Descrição / Fornecedor */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Descrição / Fornecedor
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm focus:border-[#7c3aed] focus:outline-none"
                      placeholder="Ex: Serviço realizado pela empresa Climafrio"
                      value={currentExpense.supplierName || ""}
                      onChange={(e) =>
                        setCurrentExpense({
                          ...currentExpense,
                          supplierName: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Tipo & Categoria */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        Tipo
                      </label>
                      <select
                        disabled={isViewOnly}
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                        value={currentExpense.type}
                        onChange={handleTypeChange}
                      >
                        {types.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>

                    {!isEditing && currentExpense.type === "Boleto" ? (
                      <div className="animate-in fade-in zoom-in duration-300">
                        <label className="block text-xs font-semibold text-[#7c3aed] dark:text-purple-400 mb-1 flex items-center gap-1">
                          <Layers size={14} /> Qtd. Parcelas
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="60"
                          className="w-full border-2 border-purple-100 dark:border-purple-900/50 p-2 rounded-lg text-[#7c3aed] dark:text-purple-400 font-bold focus:border-[#7c3aed] focus:outline-none bg-white dark:bg-slate-900 text-sm"
                          placeholder="Ex: 3"
                          value={installments}
                          onChange={(e) => setInstallments(Number(e.target.value))}
                        />
                      </div>
                    ) : showVehicleSelect ? (
                      <div className="animate-in fade-in zoom-in duration-300">
                        <label className="block text-xs font-semibold text-orange-600 dark:text-orange-400 mb-1 flex items-center gap-1">
                          <Truck size={14} /> Veículo
                        </label>
                        <select
                          disabled={isViewOnly}
                          className="w-full border-2 border-orange-100 dark:border-orange-900/50 p-2 rounded-lg bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-700 dark:text-slate-300 focus:border-orange-500 focus:outline-none text-sm"
                          value={currentExpense.vehicle || ""}
                          onChange={(e) =>
                            setCurrentExpense({
                              ...currentExpense,
                              vehicle: e.target.value,
                            })
                          }
                        >
                          <option value="">Selecione o veículo...</option>
                          {vehicles.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                          Categoria
                        </label>
                        <select
                          disabled={isViewOnly}
                          className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                          value={currentExpense.category}
                          onChange={(e) =>
                            setCurrentExpense({
                              ...currentExpense,
                              category: e.target.value,
                            })
                          }
                        >
                          {categories.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {((!isEditing && currentExpense.type === "Boleto") || showVehicleSelect) && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        Categoria
                      </label>
                      <select
                        disabled={isViewOnly}
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                        value={currentExpense.category}
                        onChange={(e) =>
                          setCurrentExpense({
                            ...currentExpense,
                            category: e.target.value,
                          })
                        }
                      >
                        {categories.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* PIX Copia e Cola / Chave PIX */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      PIX Copia e Cola / Chave PIX
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs font-mono"
                      placeholder="Cole a chave PIX ou código Copia e Cola"
                      value={currentExpense.pix || ""}
                      onChange={(e) =>
                        setCurrentExpense({
                          ...currentExpense,
                          pix: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Linha Digitável */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Linha Digitável
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs font-mono"
                      placeholder="Somente números"
                      value={currentExpense.linha_digitavel || ""}
                      onChange={(e) =>
                        setCurrentExpense({
                          ...currentExpense,
                          linha_digitavel: e.target.value,
                        })
                      }
                    />
                  </div>

                  {currentExpense.codigo_barras && (
                    <div className="flex justify-center bg-white p-3 rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
                      <Barcode
                        value={currentExpense.codigo_barras}
                        width={1.5}
                        height={45}
                        fontSize={13}
                        background="transparent"
                      />
                    </div>
                  )}
                </div>

                {/* Coluna da Direita: Dados Financeiros & Anexos (5 colunas) */}
                <div className="lg:col-span-5 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Dados Financeiros & Anexos
                  </h4>

                  {/* Card de Valores Principais */}
                  <div className="bg-purple-50/50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/40 rounded-xl p-4 space-y-3.5">
                    <div className="grid grid-cols-2 gap-3">
                      {/* Input de Valor */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                          Valor (R$)
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          disabled={isViewOnly}
                          className="w-full border border-purple-200 dark:border-purple-900/60 p-2.5 rounded-lg font-bold text-base text-purple-950 dark:text-purple-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none"
                          placeholder="0,00"
                          value={amountInput}
                          onChange={handleAmountInputChange}
                        />
                      </div>

                      {/* Input de Quantidade */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                          Quantidade
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          disabled={isViewOnly}
                          className="w-full border border-purple-200 dark:border-purple-900/60 p-2.5 rounded-lg font-bold text-base text-purple-950 dark:text-purple-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none"
                          placeholder="1"
                          value={currentExpense.quantidade !== undefined && currentExpense.quantidade !== null ? currentExpense.quantidade : ""}
                          onChange={(e) =>
                            setCurrentExpense({
                              ...currentExpense,
                              quantidade: e.target.value === "" ? undefined : Number(e.target.value),
                            })
                          }
                        />
                      </div>
                    </div>

                    {/* Vencimento */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                        Data de Vencimento
                      </label>
                      <input
                        type="date"
                        disabled={isViewOnly}
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm focus:border-[#7c3aed] outline-none"
                        value={currentExpense.dueDate || ""}
                        onChange={(e) =>
                          setCurrentExpense({
                            ...currentExpense,
                            dueDate: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Checkbox Já está pago */}
                    <div className="flex items-center gap-2 p-2.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <input
                        type="checkbox"
                        id="markAsPaid"
                        checked={currentExpense.status === "paid"}
                        disabled={isViewOnly}
                        onChange={(e) => {
                          const isPaid = e.target.checked;
                          const currentVal =
                            parseFloat(
                              amountInput.replace(/\./g, "").replace(",", "."),
                            ) || 0;
                          const newPaymentDate = isPaid
                            ? currentExpense.dueDate ||
                              currentExpense.createdAt ||
                              getLocalDateString()
                            : null;

                          if (isPaid) {
                            setPaidAmountInput(
                              currentVal.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }),
                            );
                          }

                          setCurrentExpense((prev) => ({
                            ...prev,
                            status: isPaid ? "paid" : "open",
                            paidAmount: isPaid ? currentVal : null,
                            paymentDate: newPaymentDate,
                          }));
                        }}
                        className="w-4 h-4 text-green-600 dark:text-green-500 rounded focus:ring-green-500 border-gray-300 dark:border-slate-600 dark:bg-slate-800 cursor-pointer"
                      />
                      <label
                        htmlFor="markAsPaid"
                        className="text-xs font-bold text-slate-700 dark:text-slate-300 select-none cursor-pointer flex items-center gap-1.5"
                      >
                        <CheckCircle
                          size={15}
                          className={
                            currentExpense.status === "paid"
                              ? "text-green-600 dark:text-green-500"
                              : "text-slate-400 dark:text-slate-500"
                          }
                        />
                        Já está pago?
                      </label>
                    </div>

                    {/* Dados de Pagamento se Pago */}
                    {currentExpense.status === "paid" && (
                      <div className="grid grid-cols-2 gap-3 bg-green-50 dark:bg-green-500/10 p-3 rounded-lg border border-green-200 dark:border-green-500/20 animate-in fade-in duration-200">
                        <div className="col-span-2 text-[10px] font-bold text-green-700 dark:text-green-400 uppercase">
                          Confirmação do Pagamento
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">
                            Valor Pago
                          </label>
                          <input
                            type="text"
                            disabled={isViewOnly}
                            className="w-full border border-slate-300 dark:border-slate-700 p-1.5 rounded bg-white dark:bg-slate-900 disabled:bg-slate-100 dark:disabled:bg-slate-800/50 font-bold text-green-700 dark:text-green-500 text-xs"
                            value={paidAmountInput}
                            onChange={handlePaidAmountInputChange}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">
                            Data Pagto
                          </label>
                          <input
                            type="date"
                            disabled={isViewOnly}
                            className="w-full border border-slate-300 dark:border-slate-700 p-1.5 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs"
                            value={currentExpense.paymentDate || ""}
                            onChange={(e) =>
                              setCurrentExpense({
                                ...currentExpense,
                                paymentDate: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card de Anexos */}
                  <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3.5 space-y-2.5">
                    <h5 className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <Paperclip size={14} className="text-[#7c3aed]" /> Comprovantes e Anexos
                      </span>
                      {currentExpense.attachments && currentExpense.attachments.length > 0 && (
                        <span className="text-[10px] bg-purple-100 dark:bg-purple-900/40 text-[#7c3aed] dark:text-purple-300 px-2 py-0.5 rounded-full font-bold">
                          {currentExpense.attachments.length} {currentExpense.attachments.length === 1 ? 'item' : 'itens'}
                        </span>
                      )}
                    </h5>

                    {/* Lista de anexos existentes */}
                    <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                      {currentExpense.attachments?.map((att) => (
                        <div
                          key={att.id}
                          className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm text-xs"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            {att.thumbnailUrl ? (
                              <img 
                                src={att.thumbnailUrl} 
                                alt={att.name} 
                                className="w-8 h-8 object-cover rounded cursor-pointer" 
                                onClick={() => handlePreview(att)} 
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = 'https://placehold.co/100x100/f8fafc/94a3b8?text=X';
                                }}
                              />
                            ) : (
                              <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded flex items-center justify-center text-slate-400 cursor-pointer" onClick={() => handlePreview(att)}>
                                <Paperclip size={14} />
                              </div>
                            )}
                            <div
                              className="truncate max-w-[120px] font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
                              onClick={() => handlePreview(att)}
                            >
                              {att.name}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handlePreview(att)}
                              className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
                            >
                              Ver
                            </button>
                            {!isViewOnly && (
                              <button
                                onClick={() => handleDeleteAttachment(att.id)}
                                className="text-red-400 hover:text-red-600 dark:hover:text-red-400"
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {tempAttachment && (
                        <div className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/20 rounded-lg shadow-sm text-xs animate-pulse">
                          <div className="flex items-center gap-2 overflow-hidden">
                            {tempAttachment.thumbnailUrl ? (
                              <img src={tempAttachment.thumbnailUrl} alt={tempAttachment.name} className="w-8 h-8 object-cover rounded" referrerPolicy="no-referrer" />
                            ) : tempAttachment.name.toLowerCase().endsWith('.pdf') ? (
                              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded flex items-center justify-center text-yellow-600 dark:text-yellow-500">
                                <FileText size={14} />
                              </div>
                            ) : (
                              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded flex items-center justify-center text-yellow-600 dark:text-yellow-500">
                                <Paperclip size={14} />
                              </div>
                            )}
                            <span className="truncate max-w-[120px] font-medium text-yellow-800 dark:text-yellow-400">
                              {tempAttachment.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-yellow-600 dark:text-yellow-500 font-bold">
                              Carregado
                            </span>
                            <button
                              onClick={clearTempAttachment}
                              className="text-red-400 hover:text-red-600 dark:hover:text-red-400"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {!isViewOnly && (
                      <div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/*,application/pdf"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={handleUploadClick}
                          disabled={isUploading}
                          className="w-full border border-dashed border-purple-300 dark:border-purple-800/80 bg-white dark:bg-slate-900 hover:bg-purple-50/50 dark:hover:bg-purple-950/20 text-[#7c3aed] dark:text-purple-400 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-colors"
                        >
                          <Camera size={16} />
                          {isUploading ? "Enviando arquivo..." : "Adicionar Foto ou Comprovante"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/70 border-t border-slate-200 dark:border-slate-700/80 px-6 py-3.5 flex justify-end gap-3 shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setDocumentMetadata(null);
                }}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-300 dark:hover:border-slate-600 rounded-lg transition-colors font-medium"
              >
                {isViewOnly ? "Fechar" : "Cancelar"}
              </button>
              {!isViewOnly && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || isUploading}
                  className="px-6 py-2 text-sm text-white bg-green-600 hover:bg-green-700 font-bold uppercase rounded-lg shadow-md flex items-center gap-2 disabled:opacity-50 transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isSaving
                    ? "Salvando..."
                    : "Salvar" +
                      (installments > 1 &&
                      currentExpense.type === "Boleto" &&
                      !isEditing
                        ? ` (${installments})`
                        : "")}
                </button>
              )}
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Modal de Preview do Anexo */}
      {previewUrl && (
        <ModalPortal>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 dark:bg-black/95 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-lg p-2 max-w-4xl w-full max-h-[95vh] flex flex-col relative">
            <div className="flex justify-between items-center p-2 border-b border-slate-200 dark:border-slate-800 mb-2">
              <h3 className="font-bold text-slate-700 dark:text-slate-200">
                Visualização do Anexo
              </h3>
              <button
                onClick={() => setPreviewUrl(null)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
              >
                <X size={24} className="text-slate-500 dark:text-slate-400" />
              </button>
            </div>
            <div className="flex-1 overflow-auto flex justify-center items-center bg-slate-100 dark:bg-slate-950 rounded min-h-[300px]">
              {previewUrl && (previewUrl.toLowerCase().endsWith(".pdf") || previewUrl.includes("drive.google.com/file/d/")) ? (
                <iframe
                  src={previewUrl.includes('/view') ? previewUrl.replace(/\/view.*/, '/preview') : previewUrl}
                  className="w-full h-full min-h-[500px]"
                  title="Document Preview"
                ></iframe>
              ) : previewUrl.includes("export=download") ? (
                <div className="bg-white dark:bg-slate-800 p-8 rounded-lg text-center max-w-sm">
                  <FileText size={48} className="mx-auto text-blue-500 mb-4" />
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-2">Arquivo para Download</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-6">Este tipo de arquivo não suporta visualização direta. Clique no botão abaixo para baixar.</p>
                  <a
                    href={previewUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink size={18} /> Fazer Download
                  </a>
                </div>
              ) : (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-w-full max-h-[70vh] object-contain"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://placehold.co/800x600/f8fafc/94a3b8?text=Erro+ao+carregar';
                  }}
                />
              )}
            </div>
            <div className="p-4 flex justify-center">
              {!previewUrl.includes("export=download") && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                >
                  <ExternalLink size={18} /> Visualizar em nova guia
                </a>
              )}
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default Dashboard;
